import {
  Box,
  type CustomerQuote,
  Dialog,
  type FixingIndex,
  logger,
  MultiSelect,
  NotificationVariants,
  Patch,
  Post,
  type RequiredProperties,
  Stack,
  Text,
  useCallbackRef,
  useDisclosureWithContext,
  type UseDisclosureWithContextReturn,
  useEndpointsContext,
  useGlobalToasts,
  useObservableValue,
} from '@talos/kyoko';
import { useMemo, useState } from 'react';
import { map } from 'rxjs';
import { useFixingIndices } from '../../../providers/FixingIndexProvider';
import { useCustomerQuotesToFix } from '../useCustomerQuotesToFix';
import { CustomerQuotesToFixBlotter } from './CustomerQuotesToFixBlotter';
import { FixingBlotter } from './FixingBlotter';
import { FixingPriceWarningDialog } from './FixingPriceWarningDialog';
import { canApplyFixing, type Fixing, fixingSliceActions, type FixingState } from './fixingSlice';
import { useFixingState } from './useFixingState';
import { useMarketDataForFixingState } from './useMarketDataForFixingState';
import { type FixingPriceWarning, getMidPriceFixingWarnings } from './utils';

type FixCompoundQuotePriceDialogProps = UseDisclosureWithContextReturn<CustomerQuote[]>;

function getFixingIndexLabel(fi: FixingIndex) {
  return fi.DisplayName ?? fi.Name;
}

function getFixingIndexDescription(fi: FixingIndex) {
  return fi.Description ?? '';
}

export const FixQuotePriceDialogV2 = ({ context, ...props }: FixCompoundQuotePriceDialogProps) => {
  if (!context) {
    return null;
  }

  return <FixQuotePriceDialogContent key={props.openId} context={context} {...props} />;
};

const FixQuotePriceDialogContent = ({
  context: selectedCustomerQuotes,
  ...dialogProps
}: RequiredProperties<FixCompoundQuotePriceDialogProps, 'context'>) => {
  const { add: addToast } = useGlobalToasts();
  const { fixingIndicesList, fixingIndicesByName } = useFixingIndices();

  const midPriceWarningDialog = useDisclosureWithContext<FixingPriceWarning[]>();

  // If the user has selected a specific set of quotes, that'll map to a specific set of fixing indices to provide. We do not let the user add more in this case.
  const changeSelectedFixingsDisabled = selectedCustomerQuotes.length > 0;
  const { state, dispatch } = useFixingState(selectedCustomerQuotes);

  const selectedFixingIndices = useMemo(() => state.fixings.map(f => f.fixingIndex), [state.fixings]);
  const { customerQuotesToFix, fixingDate } = useCustomerQuotesToFix({ selectedFixingIndices, selectedCustomerQuotes });

  // Start one MDS sub per symbol we are entering a fixing price for. This is used for fat-finger checking and showing the user a reference on screen.
  const multiMarketDataObs = useMarketDataForFixingState(state);

  // Pull out the current MarketDataSnapshot by symbol to use for fat-finger checking
  const mdsBySymbol = useObservableValue(
    () => multiMarketDataObs.pipe(map(msg => msg.cache)),
    [multiMarketDataObs],
    new Map()
  );

  const handleFixingChange = useCallbackRef((fixingIndex: string, value: string) => {
    dispatch(fixingSliceActions.setFixing({ fixingIndex, value }));
  });

  const handleSelectedFixingIndicesChange = useCallbackRef((newSelections: FixingIndex[]) => {
    dispatch(fixingSliceActions.updateFixingEntries({ fixingIndices: newSelections }));
  });

  const [loading, setLoading] = useState(false);
  const { orgApiEndpoint } = useEndpointsContext();

  const handleConfirm = useCallbackRef(() => {
    const warnings = getMidPriceFixingWarnings(state, mdsBySymbol);

    if (warnings.length > 0) {
      // Show the warnings to the user before continuing
      midPriceWarningDialog.open(warnings);
    } else {
      applyFixing();
    }
  });

  const applyFixing = useCallbackRef(() => {
    if (orgApiEndpoint == null) {
      return;
    }

    setLoading(true);
    Promise.all(customerQuotesToFix.map(quote => applyFixingToQuote(orgApiEndpoint, quote, state)))
      .then(() => {
        dialogProps.close();
        addToast({
          text: 'Successfully applied fixing(s).',
          variant: NotificationVariants.Positive,
          timeout: 5000,
        });
      })
      .catch(() => {
        addToast({
          text: 'Failed to apply fixing price on some quotes. Please try again.',
          variant: NotificationVariants.Negative,
          timeout: 5000,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  });

  const canApplyFixingResult = useMemo(
    () => canApplyFixing(state, customerQuotesToFix, fixingIndicesByName),
    [state, customerQuotesToFix, fixingIndicesByName]
  );

  return (
    <>
      <FixingPriceWarningDialog {...midPriceWarningDialog} onConfirm={applyFixing} />
      <Dialog
        {...dialogProps}
        width={900}
        title="Apply Fixing"
        confirmLabel="Apply Fixing"
        closeOnConfirm={false}
        closeOnClickOutside={false}
        showClose
        confirmDisabled={loading || !canApplyFixingResult.valid}
        /* If the button is disabled, pass in the disablement reason to show as a tooltip over the confirm button */
        confirmButtonTooltip={canApplyFixingResult.valid === false ? canApplyFixingResult.reason : undefined}
        onConfirmMouseOver={() => dispatch(fixingSliceActions.touchAll())}
        confirmLoading={loading}
        onConfirm={handleConfirm}
        data-testid="apply-fixing-dialog-v2"
      >
        <Stack alignItems="flex-start" gap="spacing12">
          <Stack gap="spacing2" minWidth="300px">
            <Text textAlign="left" color="colorTextImportant">
              Indices to fix
            </Text>
            <MultiSelect
              options={fixingIndicesList}
              selections={selectedFixingIndices}
              getLabel={getFixingIndexLabel}
              getDescription={getFixingIndexDescription}
              onChange={handleSelectedFixingIndicesChange}
              disabled={changeSelectedFixingsDisabled}
              placeholder="Select indices"
              data-testid="apply-fixing-dialog-index-multiselector"
            />
          </Stack>

          <Stack data-testid="apply-fixing-fixing-blotter" gap="spacing4" alignItems="flex-start" w="100%">
            <Text color="colorTextImportant">Fixing to apply to {customerQuotesToFix.length} quote(s)</Text>

            <Box h="270px" w="100%">
              <FixingBlotter
                fixings={state.fixings}
                multiMarketDataObs={multiMarketDataObs}
                onFixingChange={handleFixingChange}
                fixingDate={fixingDate}
              />
            </Box>
          </Stack>

          <Stack data-testid="apply-fixing-quotes-blotter" gap="spacing4" alignItems="flex-start" w="100%">
            <Text color="colorTextImportant">Quotes to fix ({customerQuotesToFix.length})</Text>
            <Box h="270px" w="100%">
              <CustomerQuotesToFixBlotter customerQuotesToFix={customerQuotesToFix} />
            </Box>
          </Stack>
        </Stack>
      </Dialog>
    </>
  );
};

/**
 * Given the quote you want to apply the set fixing to, and the current fixing state, will return a Promise for this
 * action being performed against the backend. This function does not do any validation, but assumes that the contents of FixingState
 * are already valid
 */
function applyFixingToQuote(orgApiEndpoint: string, cq: CustomerQuote, state: FixingState) {
  if (cq.LegSummary) {
    // This is a CQ for a compound instrument. We apply the fixing per-leg here.
    return Post(
      orgApiEndpoint,
      `/customer/quotes/${cq.RFQID}/apply-fixing`,
      {
        RFQID: cq.RFQID,
        LegFixingPrices: cq.LegSummary.map(legSummary => {
          return {
            Symbol: legSummary.Symbol,
            FixingPrice: getFixingForIndex(state, cq, legSummary.FixingDetails?.Index)?.field.value,
          };
        }),
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
  } else {
    // This is the non-compound case. The fixing is applied top-level here.
    const fixingForIndex = getFixingForIndex(state, cq, cq.FixingDetails?.Index);
    return Patch(orgApiEndpoint, `/customer/quotes/${cq.RFQID}/apply-fixing`, {
      Price: fixingForIndex?.field.value,
      FixingDetails: {
        Index: fixingForIndex?.fixingIndex.Name,
      },
    });
  }
}

function getFixingForIndex(state: FixingState, cq: CustomerQuote, indexToGrab: string | undefined): Fixing | undefined {
  const fixingForIndex = indexToGrab
    ? state.fixings.find(fixing => fixing.fixingIndex.Name === indexToGrab)
    : undefined;
  if (!fixingForIndex) {
    // If we aren't able to find the entry (shouldn't happen), just log and proceed. The backend will reject the request. There is already validation that prevents this.
    logger.error(new Error('Failed to find fixing price for index when applying fixing to a CustomerQuote'), {
      extra: {
        customerQuote: cq,
        fixingState: state.fixings.map(entry => ({
          fixingIndex: entry.fixingIndex.Name,
          symbol: entry.symbol,
          value: entry.field.value,
        })),
      },
    });
  }

  return fixingForIndex;
}
